import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FolderOpen, X, TriangleAlert } from 'lucide-react';
import { Section } from '../settingsPrimitives';
import { useTerminalConfigStore } from '../../store/terminalConfigStore';
import { isLocalAbsolutePath } from './terminalDefaultCwd';
import './TerminalProfiles.css';

async function pickFolder() {
  try {
    const { open } = await import('@tauri-apps/plugin-dialog');
    const p = await open({ directory: true, multiple: false });
    return typeof p === 'string' ? p : null;
  } catch {
    return null;
  }
}

/**
 * Settings → Extensions → Terminal → "Working directory": the Terminal's
 * default ("root") folder, stored in the device-local terminal config. New
 * tabs start there, splits follow their pane, a profile with its own folder
 * overrides it. A value that is not a local absolute path gets an inline
 * warning (Rust refuses it and starts in home).
 */
export default function TerminalDefaultCwdSetting() {
  const { t } = useTranslation();
  const value = useTerminalConfigStore((s) => s.prefs?.defaultCwd || '');
  const setDefaultCwd = useTerminalConfigStore((s) => s.setDefaultCwd);
  // Judged when the field is left, not per keystroke: `C:` on the way to
  // `C:\dev` is not a path yet, and the warning flickered (card 7e48a746).
  const [editing, setEditing] = useState(false);
  const invalid = !!value.trim() && !isLocalAbsolutePath(value);
  const label = t('terminalExt.defaultCwd', 'Default folder');

  return (
    <Section title={t('terminalExt.profileCwd', 'Working directory')}>
      <div className="settings-row" style={{ display: 'block' }}>
        <div className="settings-row-label">{label}</div>
        <div className="settings-row-desc">
          {t(
            'terminalExt.defaultCwdDesc',
            'New tabs start here. Splits follow the pane they split from. A profile with its own folder overrides this.'
          )}
        </div>
        <div className="term-field-inline" style={{ marginTop: 8 }}>
          <input
            type="text"
            className="settings-text-input flex-1 min-w-0 term-mono"
            value={value}
            onChange={(e) => setDefaultCwd(e.target.value)}
            placeholder={t('terminalExt.defaultCwdPlaceholder', 'Your home folder')}
            aria-label={label}
            aria-invalid={(invalid && !editing) || undefined}
            onFocus={() => setEditing(true)}
            onBlur={() => setEditing(false)}
            spellCheck={false}
            autoComplete="off"
          />
          <button
            type="button"
            className="term-field-btn"
            onClick={async () => {
              const p = await pickFolder();
              if (p) setDefaultCwd(p);
            }}
          >
            <FolderOpen size={13} aria-hidden />
            {t('terminalExt.browse', 'Browse…')}
          </button>
          {value && (
            <button type="button" className="term-field-btn" onClick={() => setDefaultCwd('')}>
              <X size={13} aria-hidden />
              {t('terminalExt.defaultCwdClear', 'Clear')}
            </button>
          )}
        </div>
        {invalid && !editing && (
          <div className="term-field-warn" role="alert">
            <TriangleAlert size={12} aria-hidden />
            {t(
              'terminalExt.defaultCwdInvalid',
              'Not a local folder path (like C:\\dev or /home/me/dev) — terminals will start in your home folder.'
            )}
          </div>
        )}
      </div>
    </Section>
  );
}
